/**
 * CreditsModal — open-source attributions, on the frosted-glass system.
 *
 * Lists every third-party component BisaWofa is built on, grouped by role,
 * with version, licence and what it is used for. Rendered from src/credits.js
 * so it stays in step with the generated Word document.
 *
 * Usage:  openCreditsModal();
 *
 * @module components/CreditsModal
 */

import { injectGlassStyles } from './glassForm.js';
import { CREDIT_SECTIONS, LICENSES, creditCount } from '../credits.js';

/** Licence badge colour by permissiveness — copyleft is visually distinct. */
function licenseTone(license) {
  if (/LGPL|GPL/.test(license)) return 'copyleft';
  if (/ODbL|CC-BY/.test(license)) return 'share';
  if (/Public Domain|Unlicense/.test(license)) return 'public';
  return 'permissive';
}

export function openCreditsModal() {
  injectGlassStyles();
  _ensureCreditsStyles();

  const sectionsHtml = CREDIT_SECTIONS.map((section) => {
    const rows = section.items.map((c) => {
      const tone = licenseTone(c.license);
      const ver = c.version ? `<span class="credit-version">v${_esc(c.version)}</span>` : '';
      return `
        <li class="credit-item">
          <div class="credit-item-head">
            <a class="credit-name" href="${_esc(c.url)}" target="_blank" rel="noopener noreferrer">
              ${_esc(c.name)}<i class="bi bi-box-arrow-up-right credit-ext"></i>
            </a>
            ${ver}
            <span class="credit-lic credit-lic-${tone}" title="${_esc(LICENSES[c.license] || '')}">${_esc(c.license)}</span>
          </div>
          <div class="credit-use">${_esc(c.use)}</div>
          ${c.note ? `<div class="credit-note">${_esc(c.note)}</div>` : ''}
        </li>`;
    }).join('');

    return `
      <section class="credit-section">
        <h3 class="credit-section-title">
          <i class="bi ${_esc(section.icon)}"></i> ${_esc(section.title)}
          <span class="credit-section-count">${section.items.length}</span>
        </h3>
        <ul class="credit-list">${rows}</ul>
      </section>`;
  }).join('');

  const overlay = document.createElement('div');
  overlay.className = 'glass-overlay';
  overlay.innerHTML = `
    <div class="glass-card credits-card" role="dialog" aria-modal="true" aria-label="Open source credits">
      <div class="glass-grabber" aria-hidden="true"></div>
      <header class="glass-header">
        <div class="glass-icon" aria-hidden="true"><i class="bi bi-heart"></i></div>
        <div>
          <h2 class="glass-title">Credits</h2>
          <p class="glass-subtitle">${creditCount()} open-source components</p>
        </div>
      </header>
      <div class="glass-body">
        <p class="credit-intro">
          BisaWofa is built on the work of the open-source community. We are grateful
          to everyone who made these projects freely available.
        </p>
        ${sectionsHtml}
        <p class="credit-footer-note">
          Map data © OpenStreetMap contributors, available under the Open Database Licence.
          Weather alerts courtesy of the Ghana Meteorological Agency.
          Full licence texts are distributed with the application.
        </p>
      </div>
      <footer class="glass-footer">
        <button type="button" class="glass-btn glass-btn-ghost" data-close>Close</button>
      </footer>
    </div>`;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('glass-open'));

  const close = () => {
    overlay.classList.remove('glass-open');
    setTimeout(() => overlay.remove(), 350);
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);

  overlay.addEventListener('click', (e) => {
    // Let outbound project links through untouched.
    if (e.target.closest('a')) return;
    if (e.target.closest('[data-close]') || e.target === overlay) close();
  });

  return { close };
}

let _styled = false;
function _ensureCreditsStyles() {
  if (_styled) return;
  _styled = true;
  const css = `
    .credits-card .glass-body { max-height: 68vh; overflow-y: auto; }
    .credit-intro { font-size: 0.88rem; line-height: 1.5; color: #33263a; margin-bottom: 16px; }
    .credit-section { margin-bottom: 18px; }
    .credit-section-title {
      display: flex; align-items: center; gap: 8px;
      font-size: 0.78rem; font-weight: 800; text-transform: uppercase;
      letter-spacing: 0.06em; color: #556B2F; margin: 0 0 8px;
    }
    .credit-section-count {
      font-size: 0.7rem; font-weight: 700; color: rgba(40,20,45,0.5);
      background: rgba(0,0,0,0.06); border-radius: 999px; padding: 1px 7px;
    }
    .credit-list { list-style: none; margin: 0; padding: 0;
      display: flex; flex-direction: column; gap: 8px; }
    .credit-item {
      padding: 10px 12px; border-radius: 12px;
      background: rgba(255,255,255,0.55); border: 1px solid rgba(0,0,0,0.06);
    }
    .credit-item-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .credit-name {
      font-weight: 700; color: #8B008B; text-decoration: none; font-size: 0.94rem;
      display: inline-flex; align-items: center; gap: 4px;
    }
    .credit-name:hover { text-decoration: underline; }
    .credit-ext { font-size: 0.68rem; opacity: 0.55; }
    .credit-version {
      font-size: 0.72rem; font-weight: 700; color: rgba(40,20,45,0.6);
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    .credit-lic {
      margin-left: auto; font-size: 0.68rem; font-weight: 800; letter-spacing: 0.02em;
      padding: 2px 8px; border-radius: 999px; white-space: nowrap; cursor: help;
    }
    .credit-lic-permissive { background: rgba(34,139,34,0.14); color: #2f6b2f; }
    .credit-lic-copyleft   { background: rgba(234,88,12,0.16);  color: #c2410c; }
    .credit-lic-share      { background: rgba(37,99,235,0.14);  color: #1d4ed8; }
    .credit-lic-public     { background: rgba(107,114,128,0.16); color: #4b5563; }
    .credit-use { margin-top: 4px; font-size: 0.83rem; line-height: 1.4; color: #33263a; }
    .credit-note {
      margin-top: 5px; font-size: 0.76rem; line-height: 1.4; color: rgba(40,20,45,0.7);
      background: rgba(255,233,106,0.28); padding: 5px 8px; border-radius: 8px;
    }
    .credit-footer-note {
      margin-top: 16px; padding-top: 12px; border-top: 1px solid rgba(0,0,0,0.08);
      font-size: 0.75rem; line-height: 1.5; color: rgba(40,20,45,0.6);
    }
  `;
  const el = document.createElement('style');
  el.id = 'credits-modal-styles';
  el.textContent = css;
  document.head.appendChild(el);
}

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export default { openCreditsModal };
